import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  MenuItem,
  Box,
  Stack,
  Divider,
  Grid,
  IconButton,
  Alert,
} from '@mui/material';
import Papa from 'papaparse';
import { Position } from '@staffedupusa/shared-components';
import { Close, Upload } from '@mui/icons-material';
import { useCreateApplication } from '@features/applications/hooks/useCreateApplication';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';

type TeamMember = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  position: string;
  matchedPositionId?: number | string;
};

interface UploadTeamDialogProps {
  open: boolean;
  onClose: () => void;
  positions: Position[];
}
const REQUIRED_HEADERS = [
  'First Name',
  'Last Name',
  'Email',
  'Cell Phone',
  'Position',
];

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
const UploadTeamDialog: React.FC<UploadTeamDialogProps> = ({
  open,
  onClose,
  positions,
}) => {
  const [error, setErrorText] = useState<string>('');
  const [teamData, setTeamData] = useState<TeamMember[]>([]);
  const [positionSelections, setPositionSelections] = useState<
    Record<number, number | string>
  >({});
  const { createApplication, isCreateApplicationLoading } =
    useCreateApplication({
      setErrorText,
      isJson: true
    });
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 1,
      complete: (previewResult) => {
        const headers = Object.keys(previewResult.data[0] || {});
        const missingHeaders = REQUIRED_HEADERS.filter(
          (h) => !headers.includes(h),
        );

        if (missingHeaders.length > 0) {
          setErrorText(
            `Missing required column(s): ${missingHeaders.join(', ')}`,
          );
          return;
        }

        // Full parse with validation
        Papa.parse<TeamMember>(file, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const validRows: TeamMember[] = [];
            const selections: Record<number, number | string> = {};

            result.data.forEach((row: any, idx: number) => {
              const first_name = row['First Name']?.trim();
              const last_name = row['Last Name']?.trim();
              const email = row.Email?.trim();
              const phone = row['Cell Phone']?.trim();
              const position = row['Position']?.trim();

              if (
                first_name &&
                last_name &&
                email &&
                phone &&
                position &&
                isValidEmail(email)
              ) {
                validRows.push({
                  first_name,
                  last_name,
                  email,
                  phone,
                  position,
                });

                const matched = positions.find(
                  (p) => p.s_title.toLowerCase() === position.toLowerCase(),
                );
                if (matched) {
                  selections[idx] = matched.id;
                } else {
                  selections[idx] = ''; // Default to empty if no match
                }
                if (!matched) selections[validRows.length - 1] = '';
              }
            });

            if (validRows.length === 0) {
              setErrorText(
                'No valid rows found. Please check the file for errors.',
              );
              return;
            }

            setTeamData(validRows);
            setPositionSelections(selections);
            setErrorText('');
          },
          error: (error) => {
            setErrorText('Error parsing CSV file: ' + error.message);
          },
        });
      },
      error: (error) => {
        setErrorText('Error reading file: ' + error.message);
      },
    });
  };

  const handleSelectionChange = (index: number, value: string) => {
    setPositionSelections((prev) => ({ ...prev, [index]: value }));
  };

  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success('Team member added successfully');
          setErrorText('');
          onClose();
          // reset();
        } else {
          setErrorText(data.message);
        }
      },
    };
    const finalData = teamData.map((row, idx) => {
      const matched = positions.find(
        (p) => p.s_title.toLowerCase() === row.position.toLowerCase(),
      );
      return {
        ...row,
        hired: true,
        job_listing_ids: String(positionSelections[idx]),
      };
    });

    createApplication(
      {
        is_multiple: true,
        team_members: finalData,
      },
      options,
    );
    // onClose();
  };
  const isSubmitDisabled =
    teamData.length === 0 ||
    Object.values(positionSelections).some((v) => v === '');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={teamData.length > 0 ? 'md' : 'sm'}
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={onClose}
        >
          <Close />
        </IconButton>
        <br />
        <Typography variant="h4">Upload Team Member CSV</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<Upload />}
            sx={{ alignSelf: 'center' }}
          >
            Upload CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileUpload}
            />
          </Button>

          <Typography
            sx={{ alignSelf: 'center' }}
            variant="body2"
            color="text.secondary"
          >
            Required columns: <strong>First name</strong>,{' '}
            <strong>Last name</strong>, <strong>Email</strong>,{' '}
            <strong>Cell Phone</strong>, <strong>Position</strong>
          </Typography>

          {teamData.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2} sx={{ fontWeight: 'bold', mb: 1 }}>
                <Grid item xs={2}>
                  First Name
                </Grid>
                <Grid item xs={2}>
                  Last Name
                </Grid>
                <Grid item xs={2}>
                  Email
                </Grid>
                <Grid item xs={2}>
                  Phone
                </Grid>
               
                <Grid item xs={4}>
                  Status / Assign
                </Grid>
              </Grid>

              {teamData.map((row, idx) => {
                const matched = positions.filter(item => item.status !== 4).find(
                  (p) => p.s_title.toLowerCase() === row.position.toLowerCase(),
                );

                return (
                  <Grid
                    container
                    spacing={2}
                    key={idx}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={2}>
                      <Typography>{row.first_name}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography>{row.last_name}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          wordBreak: 'break-all',
                          maxWidth: '100%',
                        }}
                      >
                        {row.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography>{row.phone}</Typography>
                    </Grid>
                    
                    <Grid item xs={4}>
                      {!matched ? (
                        <TextField
                          select
                          label="Assign Position"
                          size="small"
                          fullWidth
                          value={positionSelections[idx] || ''}
                          onChange={(e) =>
                            handleSelectionChange(idx, e.target.value)
                          }
                        >
                          {positions.filter(item => item.status !== 4).map((pos) => (
                            <MenuItem key={pos.id} value={pos.id}>
                              {pos.s_title}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <Typography
                          sx={{ fontWeight: 500 }}
                        >
                            {matched.s_title}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: 'center', marginBottom: '20px', mt: 2 }}
      >
        <Button
          variant="outlined"
          color="secondary"
          sx={{ width: '200px', fontSize: '1rem' }}
          onClick={onClose}
          disabled={isCreateApplicationLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isCreateApplicationLoading}
          color="primary"
          variant="contained"
          sx={{
            width: '200px',
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isCreateApplicationLoading}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadTeamDialog;
